// react
import { useCallback, useEffect, useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircle from '@mui/icons-material/CheckCircle';
import DoDisturbAlt from '@mui/icons-material/DoDisturbAlt';
import Error from '@mui/icons-material/Error';
import Help from '@mui/icons-material/Help';
import Info from '@mui/icons-material/Info';
import Pending from '@mui/icons-material/Pending';

// material ui
import { Icon, Tooltip } from '@mui/material';

// api
import { usePostDatahubIntegrationAccountRetryMutation } from '@/api/datahub-integration/datahubIntegrationApiSlice';

const DatahubIntegrationAccountStatus = ({ datahubIntegrationAccount }) => {
  const { t } = useTranslation('datahub-integration');
  const { ImplementationStatus, IsAuthorized } = datahubIntegrationAccount;

  const [postDatahubIntegrationAccountRetry] =
    usePostDatahubIntegrationAccountRetryMutation();

  const retryPolling = useCallback(
    async function retryPollingInner(maxRetries) {
      if (maxRetries <= 0) return;

      const response = await postDatahubIntegrationAccountRetry({
        recalc_account_id: datahubIntegrationAccount.ReCalcAccountId,
        cvr_number: datahubIntegrationAccount.CustomerCVR,
      }).unwrap();

      const status = (response?.ImplementationStatus ?? '').toLowerCase();
      if (status.includes('unavailableerror')) {
        setTimeout(() => retryPollingInner(maxRetries - 1), 5000);
      }
    },
    [
      postDatahubIntegrationAccountRetry,
      datahubIntegrationAccount.ReCalcAccountId,
      datahubIntegrationAccount.CustomerCVR,
    ],
  );

  useEffect(() => {
    const status = (
      datahubIntegrationAccount.ImplementationStatus ?? ''
    ).toLowerCase();

    if (status.includes('unavailableerror')) {
      retryPolling(5);
    } else if (!datahubIntegrationAccount.IsAuthorized) {
      retryPolling(1);
    }
  }, [
    datahubIntegrationAccount.ImplementationStatus,
    datahubIntegrationAccount.IsAuthorized,
    retryPolling,
  ]);

  const { icon, label } = useMemo(() => {
    const status = (ImplementationStatus ?? '').toLowerCase();

    if (status.includes('unavailableerror')) {
      return {
        icon: <Help fontSize="medium" color="warning" />,
        label: t('status.unavailable'),
      };
    }
    if (!IsAuthorized) {
      return {
        icon: <DoDisturbAlt fontSize="medium" color="error" />,
        label: t('status.missing-access'),
      };
    }
    if (status.includes('new')) {
      return {
        icon: <Info fontSize="medium" color="info" />,
        label: t('status.new'),
      };
    }
    if (status.includes('processing')) {
      return {
        icon: <Pending fontSize="medium" color="info" />,
        label: t('status.processing'),
      };
    }
    if (status.includes('active')) {
      return {
        icon: <CheckCircle fontSize="medium" color="success" />,
        label: t('status.success'),
      };
    }
    if (status.includes('manualerror')) {
      return {
        icon: <Error fontSize="medium" color="warning" />,
        label: t('status.manual-error'),
      };
    }
    return {
      icon: <Error fontSize="medium" color="warning" />,
      label: t('status.unexpected-error'),
    };
  }, [ImplementationStatus, IsAuthorized, t]);

  return (
    <Tooltip disableInteractive placement="top-start" title={label}>
      <Icon sx={{ overflow: 'visible' }}>{icon}</Icon>
    </Tooltip>
  );
};

export default DatahubIntegrationAccountStatus;
